import * as PIXI from "pixi.js"
import PixiScene from "../PixiScene";
import PixiCard from "./PixiCard";
import PixiType from "./PixiType";
import PixiSelector from "./PixiSelector";
import {ICardM, IPileM} from "./model/MTypes";
//@ts-ignore
import { OutlineFilter, SepiaFilter } from 'pixi-filters';
import {Player} from "../../../../../../types";
//@ts-ignore
import store from "../../../../../store";
// ColorOverlayFilter, DropShadowFilter, GlowFilter, KawaseBlurFilter, OutlineFilter, SepiaFilter

interface ICard {
    backface_src: string;
    face_src: string;
    flipped: boolean;
    id: number;
    title: string;
    width: number;
    height: number;
    type: number;
}

interface IPile {
    id: number;
    title: string;
    width: number;
    height: number;
}

interface IState {
    scale: number;
    x: number;
    y: number;
}

export default class PixiPile extends PIXI.Container {

    public static dx = 0;
    public static dy = 0;
    public cardList: Array<PixiCard> = [];
    public isUpdated: boolean = false;
    public readonly objType = PixiType.GAME_OBJ_PILE;
    public state: IState = { scale: 1, x: 0, y: 0 };

    private readonly scene: PixiScene;
    private readonly config: IPileM;
    private depth = 1;
    private isBlocked: boolean = false;
    private isLocked: boolean = false;
    private isMasterLocked: boolean = false;
    private pileList: Array<any> = [];
    private outlineFilter = new OutlineFilter(1, 0x666666);
    private sepiaFilter = new PIXI.filters.ColorMatrixFilter();
    private isInUsage: boolean = false;
    private box = new PIXI.Graphics();
    private flipTop: PixiCard | undefined;

    private constructor(scene: PixiScene, config: any) {
        super();
        this.scene = scene;
        this.config = config;

        this.addChild(this.box);
        this.box.visible = false;

        if (scene.isInAdminMode) {
            if (config.onTable) {
                this.init(config);
            }
        } else {
            this.init(config);
        }

        const selFilter = this.scene.outlineFilter;
        this.filters = [selFilter];
    }

    /**
     * Инициализация
     */
    private init(config: any) {
        this.name = config.title;
        this.locked = config.locked;
        this.cardList = [];
        const kFigma = 0.8 / 150; /* делаем, как в Фигме */

        // const blurFilter = new PIXI.filters.BlurFilter();
        // const colorOverlayFilter = new ColorOverlayFilter(0x666666);
        // const dropShadowFilter = new DropShadowFilter({ alpha: 1, blur: 1, distance: 2, rotation: 90, });
        // const kawaseBlurFilter = new KawaseBlurFilter(4, 3, true);

        const outlineFilter = this.outlineFilter;
        const sepiaFilter = this.sepiaFilter;
        this.sepiaFilter.sepia(false);

        const pileList = Array.from(config.cards), pileLength = pileList.length;
        PixiPile.dy = config.height * kFigma;
        this.pileList = pileList;
        this.depth = Math.min(6, pileLength);
        // this.depth = pileLength > 10 ? Math.min(6, pileLength) :
        //     pileLength > 1 ? Math.min(4, pileLength) : 1;

        // PixiPile.dx = 128; PixiPile.dy = 128; //TEST
        this.box.clear();
        this.scene.tableContainer.addCard(this, config.masterLocked);
        this.x = config.x;
        this.y = config.y;

        const nRealCards = 3; //3
        const cloneConf = pileLength >= nRealCards ? (pileList[pileLength - nRealCards] as ICardM) : null;

        const createCard = (curConf:ICardM, i:number, isFlipTop = false): PixiCard => {
            const iLast = pileLength - 1 - i;
            const cardConf = {...curConf};
            if (!isFlipTop && iLast >= nRealCards && cloneConf) {
                cardConf.face_src = cloneConf.face_src;
                cardConf.backface_src = cloneConf.backface_src;
            }
            const card = this.scene.createCard(cardConf as ICard) as PixiCard;
            if (card) {
                if (!isFlipTop) {
                    this.cardList.push(card);
                }
                card.parentPile = this;
                card.pileZ = iLast;
                let blurred = false;

                this.scene.setAttr(card, {
                    cardId: cardConf.id,
                    pileId: config.id,
                    x: 0,
                    y: 0,
                    width:  cardConf.width,
                    height: cardConf.height,
                    zone: '',
                    size: 1,
                });

                if (iLast <= this.depth || isFlipTop) {
                    if (iLast === 0) {
                        if (this.scene.isInAdminMode || !config.masterLocked) {
                            this.scene.addInteractivity(card);
                        }
                        this.scene.pileMap.set(config.id, card);
                    }
                    else if (iLast === this.depth) {
                        blurred = true;
                        card.alpha = 0;
                    }
                    else {
                        blurred = true;
                    }

                    this.scene.tableContainer.addCard(card, config.masterLocked);
                    card.scale.set(1);
                    card.x = config.x + config.width /2;
                    card.y = config.y + config.height/2;
                    card.x += PixiPile.dx * iLast;
                    card.y += PixiPile.dy * iLast;
                    this.scene.setAttr(card, { size: pileLength, zone: 'table' });

                    if (pileLength > 1 && !isFlipTop) {
                        card.blurred = blurred;
                    }

                    if (iLast === 0) {
                        this.state.x = card.x;
                        this.state.y = card.y;
                    }
                }

                if (iLast < this.depth) {
                    let boxX = PixiPile.dx * iLast; //config.x +
                    let boxY = PixiPile.dy * iLast; //config.y +
                    this.box.beginFill(0xffffff);
                    this.box.drawRect(boxX, boxY, config.width, config.height);
                    this.box.endFill();
                }
            }

            this.topCard.rotate(config.angle ?? 0, false);

            card.getInit().then(card => {
                card.applyBlur(sepiaFilter, outlineFilter);
            });
            return card;
        };

        pileList.forEach((curConf:any, i:number) => createCard(curConf as ICardM, i));
        if (pileLength > 1) {
            this.flipTop = createCard(pileList[0] as ICardM, 0, true);

            this.flipTop.x = this.state.x;
            this.flipTop.y = this.state.y;
            this.flipTop.alpha = 0;
            this.flipTop.angle = config.angle ?? 0;

            // this.flipTop.x = this.state.x - 256;
            // this.flipTop.y = this.state.y - 256;
            // this.flipTop.alpha = 0.42;
        }

        if (this.usedByOther) {
            this.setUsedByPlayer(this.config.usedByPlayer, '');
        }

        if (!this.scene.isInAdminMode && config.masterLocked) {
            this.isLocked = true;
            this.isMasterLocked = true;
            this.interactive = false;
            this.interactiveChildren = false;
            this.zIndex = -100;
        }
    }

    /**
     * Используется ли другим игроком
     */
    public get usedByOther(): boolean
    {
        const usedByPlayer = this.config.usedByPlayer;
        return (!!usedByPlayer && usedByPlayer !== this.scene.playerId);
    }

    /**
     * Отобразить использование другим игроком
     */
    public setUsedByPlayer(userId:string, blockerId:string): void
    {
        this.isInUsage = false;
        this.isBlocked = false;
        if (userId) {
            if (userId !== this.scene.playerId) {
                this.isInUsage = true;
                if (blockerId && blockerId !== this.scene.playerId) {
                    this.isBlocked = true;
                }
                const playerColor = (id: string) => {
                    const players: Array<Player> = Object.values(store.getters.getPlayers);
                    const player = players.find(player => player.id === id);
                    return player ? parseInt(player.color.code.slice(1), 16) : 0xffffff;
                }
                this.topCard.getInit().then(card => card.setFilter(new OutlineFilter(4, playerColor(userId)), true));
                PixiSelector.removePiles([this.topCard.pileId]);
            }
        }
        else {
            this.topCard.getInit().then(card => card.setFilter(this.scene.outlineFilter));
        }
        // this.topCard.getInit().then(card => card.paint(this.isBlocked));
    }

    /**
     * Верхняя карта в пачке
     */
    public get topCard(): PixiCard
    {
        return this.cardList[this.cardList.length - 1];
    }

    /**
     * Пачка находится под управлением какого-то игрока
     */
    public inUsage(): boolean
    {
        return this.isInUsage;
    }

    /**
     * Пачка находится под управлением какого-то игрока и заблокирована
     */
    public inUsageBlocked(): boolean
    {
        return this.isBlocked;
    }

    /**
     * Пачка находится под мастер-локом
     */
    public inPermanentLock(): boolean
    {
        return this.isMasterLocked;
    }

    /**
     * Пачка: обновить вид
     */
    public update(): void
    {
        if (!this.isUpdated && this.pileList.length > 1) {
            this.isUpdated = true;
            this.cardList.forEach((card, i) => {
                let iLast = this.pileList.length - 1 - i;
                if (0 < iLast && iLast <= this.depth) {
                    card.x -= PixiPile.dx;
                    card.y -= PixiPile.dy;
                    card.alpha = 1;
                }
                if (1 === iLast) {
                    card.art.filters = [];
                    card.filters = [ this.outlineFilter ];
                }
            });
        }
    }

    public get pileId(): number
    {
        return this.config.id;
    }

    /**
     * Блокировка
     */
    public set locked(value: boolean)
    {
        this.isLocked = value;
    }

    public get locked() {
        return this.isLocked;
    }

    public getPileLength(): number
    {
        return this.pileList.length;
    }

    public getXY(i: number): { x: number, y: number }
    {
        let iLast = this.pileList.length - 1 - i;
        return { x: PixiPile.dx * iLast, y: PixiPile.dy * iLast };
    }

    /**
     * Перемещение выделенной пачки
     */
    public moveRest(x: number, y: number): void
    {
        this.x = x - this.config.width /2;
        this.y = y - this.config.height/2;
        if (this.pileList.length > 1) {
            this.cardList.forEach((it, i) => {
                let iLast = this.pileList.length - 1 - i;
                if (0 < iLast && iLast <= this.depth) {
                    let delta = this.getXY(i);
                    it.x = x + delta.x;
                    it.y = y + delta.y;
                }
            });
        }
        if (this.flipTop) {
            this.flipTop.x = x;
            this.flipTop.y = y;

            // this.flipTop.x = x - 256;
            // this.flipTop.y = y - 256;
        }
        this.state.x = x;
        this.state.y = y;
    }

    /**
     * Вращение карты
     */
    public rotateRest(angle: number) {
        this.angle = angle;
        if (this.pileList.length > 1) {
            this.cardList.forEach((it, i) => {
                let iLast = this.pileList.length - 1 - i;
                if (0 < iLast && iLast <= this.depth) {
                    it.angle = angle;
                }
            });
        }
        if (this.flipTop) {
            this.flipTop.angle = angle;
        }
    }

    /**
     * Скрыть/показать карты стопки
     */
    public showRest(value: boolean): void
    {
        this.visible = value;
        if (this.pileList.length > 1) {
            this.cardList.forEach((it, i) => {
                let iLast = this.pileList.length - 1 - i;
                if (0 <= iLast && iLast <= this.depth) {
                    it.visible = !iLast || value;
                    it.alpha = it.visible ? 1 : 0;
                    if (value) {
                        this.scene.moveToTop(it);
                    }
                }
            });
        }
        if (this.flipTop) {
            this.scene.moveToTop(this.flipTop);
        }
    }

    /**
     * Скрыть/показать блюр
     */
    public blurRest(value: boolean): void
    {
        if (this.pileList.length > 1) {
            this.cardList.forEach((card, i) => {
                card.blurred = value;
                card.applyBlur(this.sepiaFilter, this.outlineFilter);
            });
        }
    }

    /**
     * Скрыть/показать карты стопки в админ-режиме при изменении флага onTable
     */
    public toggleCardVisibilityInAdminMode(visibilityObj: {[key: number]: boolean}): void
    {
        this.cardList.forEach((it, i) => {
            it.visible = visibilityObj[it.config.id];
            it.filters = [];
            this.scene.addInteractivity(it);
        });
    }

    /**
     * Перевернуть карты стопки
     */
    public flipRest(value?: boolean): void
    {
        if (this.pileList.length > 1) {
            this.cardList.forEach((it, i) => {
                let iLast = this.pileList.length - 1 - i;
                if (0 < iLast && iLast <= this.depth) {
                    it.flip(value);
                }
            });
        }
    }

    /**
     * Удалить карты стопки
     */
    public dropRest(): void
    {
        this.scene.tableContainer.removeCard(this);
        if (this.pileList.length > 1) {
            this.cardList.forEach((it, i) => {
                let iLast = this.pileList.length - 1 - i;
                if (0 < iLast && iLast <= this.depth) {
                    it.parentPile = null;
                    this.scene.tableContainer.removeCard(it);
                }
            });
        }
        if (this.flipTop) {
            window.setTimeout(() => {
                if (this.flipTop) {
                    this.scene.tableContainer.removeCard(this.flipTop);
                    this.flipTop = undefined;
                }
            }, 0);
        }
    }

    /**
     * Взять N карт
     */
    public takeNCards(num: number): Array<number>
    {
        const N1 = this.cardList.length - 1;
        const list = this.cardList
            .map((card, i) => (N1 - i < num) ? card.cardId : -1)
            .filter(id => id > 0)
        return list;
    }

    /**
     * Показать обводку пачки
     */
    public showOutline(value: boolean): void
    {
        this.box.visible = value;
    }

    /**
     * Для отладки
     */
    public get cardListInfo(): string
    {
        return '' + this.cardList.map(card => card.cardId);
    }

    /**
     * Размер пачки
     */
    public get cardListLength(): number
    {
        return this.cardList.length;
    }

    /**
     * Создание пачки из конфига
     */
    public static create(conf: IPileM, scene: PixiScene): PixiPile
    {
        return new PixiPile(scene, conf);
    }

    /**
     * Получить топ для флипа
     */
    public get flipTopCard() {
        return this.flipTop;
    }

}
